/**
 * Left-side marketing panel for the PRM-rendered login (ADR-0010).
 *
 * Renders directly into the parent `<aside class="demo-theme relative
 * ...">` in `routes/login.tsx`. The `.demo-theme` scope on the aside
 * gives this subtree access to the inner-panel utility classes
 * (`.wordmark`, `.chip`, `.display`, `.mono-xs`, `.pill`, `.dot`)
 * defined in `apps/web/src/demo/theme.css` without applying that
 * theme to the rest of the app.
 *
 * The right side stays exactly as-is in routes/login.tsx — we don't
 * add the wireframe's "Continue with Microsoft" button because it
 * routes nowhere and would be misleading for users.
 */
import heroScalable from "@/assets/hero-scalable.png";
import heroSecure from "@/assets/hero-secure.png";
import heroSovereign from "@/assets/hero-sovereign.png";

import { Logo } from "../Logo";

const FEATURES = [
  { src: heroSovereign, k: "Sovereign" },
  { src: heroSecure, k: "Secure" },
  { src: heroScalable, k: "Scalable" },
];

const CERTS = ["NVIDIA Cloud Partner", "Dell Titanium", "ISO 27001", "SOC 2", "PSPF", "SCEC"];

export function LoginMarketingPanel() {
  return (
    <>
      <div style={{ display: "flex", alignItems: "center" }}>
        <Logo className="h-6" />
      </div>

      <div style={{ position: "relative", maxWidth: 560 }}>
        <div className="chip accent" style={{ marginBottom: 22 }}>
          <span className="dot ok" aria-hidden="true" />
          Partner Portal
        </div>
        <h2
          className="display"
          style={{
            // Responsive so the longer copy stays ~2 lines across the lg range:
            // full 44px on wide viewports, scaling down on narrow lg where the
            // split-screen panel is tightest (a fixed 44px wraps to 4 lines there).
            fontSize: "clamp(30px, 3.4vw, 44px)",
            fontWeight: 500,
            lineHeight: 1.05,
            margin: 0,
            letterSpacing: "-0.03em",
          }}
        >
          Sovereign AI Infrastructure,
          <br />
          <span style={{ color: "var(--rd-accent)" }}>now in your sales pipeline.</span>
        </h2>
        <p
          style={{
            color: "var(--ink-2)",
            fontSize: 15,
            lineHeight: 1.6,
            marginTop: 18,
            maxWidth: 430,
          }}
        >
          Everything you need to become an official ResetData reseller. Register deals, access
          training and resources and more.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 16,
            marginTop: 36,
          }}
        >
          {FEATURES.map(({ src, k }) => (
            <div key={k} style={{ padding: "14px 0", borderTop: "1px solid var(--line)" }}>
              <img src={src} alt="" aria-hidden="true" width={56} height={56} draggable={false} />
              <div className="mono-xs" style={{ marginTop: 8 }}>
                {k}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="mono-xs" style={{ marginBottom: 10 }}>
          Certifications &amp; partnerships
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {CERTS.map((c) => (
            <span key={c} className="pill">
              {c}
            </span>
          ))}
        </div>
      </div>

      {/* Decorative grid bg — absolute-positioned inside the parent
          aside (which has `relative`). pointerEvents:none so it doesn't
          intercept clicks on the marketing content above. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(to right, rgba(225, 29, 72, 0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(225, 29, 72, 0.04) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(circle at 30% 40%, black 20%, transparent 70%)",
          pointerEvents: "none",
          zIndex: -1,
        }}
      />
    </>
  );
}
